// From https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob, needed for Safari:
if (!HTMLCanvasElement.prototype.toBlob) {
  Object.defineProperty(HTMLCanvasElement.prototype, "toBlob", {
    value: function (callback: BlobCallback, type?: string, quality?: number) {
      const binStr = atob(this.toDataURL(type, quality).split(",")[1])
      const len = binStr.length
      const arr = new Uint8Array(len)

      for (let i = 0; i < len; i++) {
        arr[i] = binStr.charCodeAt(i)
      }

      callback(new Blob([arr], { type: type || "image/png" }))
    },
  })
}

declare global {
  interface Window {
    webkitURL: typeof URL
  }
}

window.URL = window.URL || window.webkitURL

// EXIF orientation constants
const EXIF_ORIENTATION = {
  NOT_JPEG: -2,
  NO_DATA: -1,
  NORMAL: 1,
  FLIP_HORIZONTAL: 2,
  ROTATE_180: 3,
  FLIP_VERTICAL: 4,
  ROTATE_90_FLIP_HORIZONTAL: 5,
  ROTATE_90: 6,
  ROTATE_90_FLIP_VERTICAL: 7,
  ROTATE_270: 8,
} as const

type ExifOrientation = (typeof EXIF_ORIENTATION)[keyof typeof EXIF_ORIENTATION]

// Modified from https://stackoverflow.com/a/32490603, cc by-sa 3.0
// -2 = not jpeg, -1 = no data, 1..8 = orientations
const getExifOrientation = (
  file: File,
  callback: (orientation: ExifOrientation) => void,
): void => {
  // Suggestion from http://code.flickr.net/2012/06/01/parsing-exif-client-side-using-javascript-2/:
  let slicedFile: Blob
  if (file.slice) {
    slicedFile = file.slice(0, 131072)
  } else if (
    (file as File & { webkitSlice?: (start: number, end: number) => Blob })
      .webkitSlice
  ) {
    slicedFile = (
      file as File & { webkitSlice: (start: number, end: number) => Blob }
    ).webkitSlice(0, 131072)
  } else {
    slicedFile = file
  }

  const reader = new FileReader()
  reader.onload = (e: ProgressEvent<FileReader>) => {
    if (!e.target?.result) {
      callback(EXIF_ORIENTATION.NO_DATA)
      return
    }

    const view = new DataView(e.target.result as ArrayBuffer)
    if (view.getUint16(0, false) !== 0xffd8) {
      callback(EXIF_ORIENTATION.NOT_JPEG)
      return
    }

    const length = view.byteLength
    let offset = 2

    while (offset < length) {
      const marker = view.getUint16(offset, false)
      offset += 2

      if (marker === 0xffe1) {
        if (view.getUint32((offset += 2), false) !== 0x45786966) {
          callback(EXIF_ORIENTATION.NO_DATA)
          return
        }
        const little = view.getUint16((offset += 6), false) === 0x4949
        offset += view.getUint32(offset + 4, little)
        const tags = view.getUint16(offset, little)
        offset += 2

        for (let i = 0; i < tags; i++) {
          if (view.getUint16(offset + i * 12, little) === 0x0112) {
            callback(
              view.getUint16(offset + i * 12 + 8, little) as ExifOrientation,
            )
            return
          }
        }
      } else if ((marker & 0xff00) !== 0xff00) break
      else offset += view.getUint16(offset, false)
    }
    callback(EXIF_ORIENTATION.NO_DATA)
  }
  reader.readAsArrayBuffer(slicedFile)
}

// Derived from https://stackoverflow.com/a/40867559, cc by-sa
const imgToCanvasWithOrientation = (
  img: HTMLImageElement,
  rawWidth: number,
  rawHeight: number,
  orientation: ExifOrientation,
): HTMLCanvasElement => {
  const canvas = document.createElement("canvas")

  if (orientation > 4) {
    canvas.width = rawHeight
    canvas.height = rawWidth
  } else {
    canvas.width = rawWidth
    canvas.height = rawHeight
  }

  if (orientation > 1) {
    console.log(`EXIF orientation = ${orientation}, rotating picture`)
  }

  const ctx = canvas.getContext("2d")
  if (!ctx) {
    throw new Error("Unable to get canvas context")
  }

  switch (orientation) {
    case EXIF_ORIENTATION.FLIP_HORIZONTAL:
      ctx.transform(-1, 0, 0, 1, rawWidth, 0)
      break
    case EXIF_ORIENTATION.ROTATE_180:
      ctx.transform(-1, 0, 0, -1, rawWidth, rawHeight)
      break
    case EXIF_ORIENTATION.FLIP_VERTICAL:
      ctx.transform(1, 0, 0, -1, 0, rawHeight)
      break
    case EXIF_ORIENTATION.ROTATE_90_FLIP_HORIZONTAL:
      ctx.transform(0, 1, 1, 0, 0, 0)
      break
    case EXIF_ORIENTATION.ROTATE_90:
      ctx.transform(0, 1, -1, 0, rawHeight, 0)
      break
    case EXIF_ORIENTATION.ROTATE_90_FLIP_VERTICAL:
      ctx.transform(0, -1, -1, 0, rawHeight, rawWidth)
      break
    case EXIF_ORIENTATION.ROTATE_270:
      ctx.transform(0, -1, 1, 0, 0, rawWidth)
      break
  }

  ctx.drawImage(img, 0, 0, rawWidth, rawHeight)
  return canvas
}

// Interface for future use if we want to pass options as an object
// interface ImageOptimizationOptions {
//   acceptFileSize: number
//   maxWidth: number
//   maxHeight: number
//   quality: number
// }

export const reduceFileSize = (
  file: File,
  acceptFileSize: number,
  maxWidth: number,
  maxHeight: number,
  quality: number,
  callback: (result: File | Blob) => void,
): void => {
  if (file.size <= acceptFileSize) {
    callback(file)
    return
  }

  const img = new Image()

  img.onerror = () => {
    URL.revokeObjectURL(img.src)
    callback(file)
  }

  img.onload = () => {
    URL.revokeObjectURL(img.src)

    getExifOrientation(file, (orientation) => {
      let w = img.width
      let h = img.height

      const scale =
        orientation > 4
          ? Math.min(maxHeight / w, maxWidth / h, 1)
          : Math.min(maxWidth / w, maxHeight / h, 1)

      h = Math.round(h * scale)
      w = Math.round(w * scale)

      const canvas = imgToCanvasWithOrientation(img, w, h, orientation)

      canvas.toBlob(
        (blob) => {
          if (blob) {
            console.log(`Resized image to ${w}x${h}, ${blob.size >> 10}kB`)
            callback(blob)
          } else {
            console.error("Failed to create blob from canvas")
            callback(file)
          }
        },
        "image/jpeg",
        quality,
      )
    })
  }

  img.src = URL.createObjectURL(file)
}
